import { useBreakpointDown } from '@travel-frontend/core';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import * as ActionTypes from '../../../../../components/SearchForm/components/AirportsField/actionTypes.js';
import { useDispatchAirportFieldEvent } from '../../../../../components/SearchForm/components/AirportsField/hooks/useDispatchAirportFieldEvent.js';
import { useGetNextValue } from '../../../../../components/SearchForm/components/AirportsField/hooks/useGetNextValue.js';
import { useRetriveData } from '../../../../../components/SearchForm/components/AirportsField/hooks/useRetriveData.js';
import { useAirportsWidgetState } from '../../../../../components/SearchForm/components/AirportsWidget/hooks/useAirportsWidgetState.js';
import { useFilteredAirports } from '../../../../../components/SearchForm/components/AirportsWidget/hooks/useFilteredAirports.js';
import { isAllAirportNearbyChecked } from '../../../../../components/SearchForm/components/AirportsWidget/utils/nearbyAirports.js';
import { useWidgetOverlay } from '../../../../../components/SearchForm/hooks/useWidgetOverlay.js';
// TODO: Refactor - this hook has too much logic, should be split into smaller hooks with single responsibility
export const useAirportsField = ({
  value: controlledValue,
  config: {
    airports,
    countryCode,
    forcedNeutralLabel,
    enableSelectAll = true,
    showSuggestions = true,
    widgetHead
  },
  floatingOptions,
  invalid = false,
  dimming = false,
  disabled = false,
  layout = 'full',
  dispatch
}) => {
  /**
   * The hook is parted into 3 main parts:
   * 1. State management
   * 2. Events handlers
   * 3. Widget setup
   * */
  const [value, setValue] = useState(controlledValue);
  const [suggestionInputValue, setSuggestionInputValue] = useState('');
  const [widgetOpen, setWidgetOpen] = useState(false);
  const mdDown = useBreakpointDown('md');
  const widgetType = mdDown ? 'drawer' : 'popover';
  const {
    filteredAirports,
    noMatch
  } = useFilteredAirports(suggestionInputValue, airports);
  const getNextValue = useGetNextValue(airports, value ?? []);
  const retrieveData = useRetriveData(airports);
  const {
    isChecked
  } = useAirportsWidgetState({
    value: value ?? [],
    airports
  });
  const didValueChange = () => {
    return !isEqual(value, controlledValue);
  };
  const dispatchEvent = useDispatchAirportFieldEvent(dispatch, filteredAirports.some(airport => isAllAirportNearbyChecked(airport, isChecked)), suggestionInputValue);
  /**
   * EVENTS HANDLERS
   * */
  useEffect(() => {
    if (noMatch) {
      dispatchEvent({
        type: ActionTypes.SuggestionsNoMatch,
        value: controlledValue
      });
    }
  }, [noMatch]);
  const handleOpenChange = (open, origin) => {
    if (origin === 'close-button' && didValueChange()) {
      dispatchEvent({
        type: ActionTypes.CloseClicked,
        value: controlledValue
      });
      setValue(controlledValue);
    }
    let isDefaultPrevented = false;
    if (didValueChange() && (origin === 'apply-button' || !open && widgetType === 'popover')) {
      const dispatchedValueChangedEvent = dispatchEvent({
        type: ActionTypes.ValueChanged,
        value
      });
      isDefaultPrevented = dispatchedValueChangedEvent.isDefaultPrevented;
    }
    if (!isDefaultPrevented) {
      setWidgetOpen(open);
    }
  };
  const handleApply = () => {
    handleOpenChange(false, 'apply-button');
  };
  const handleClose = () => {
    handleOpenChange(false, 'close-button');
  };
  const handleReset = () => {
    dispatchEvent({
      type: ActionTypes.ResetClicked,
      value: []
    });
    setValue([]);
  };
  const handleSuggestionInputChange = input => {
    setSuggestionInputValue(input);
  };
  const handleCheck = event => {
    const newValue = getNextValue(event.currentTarget.value);
    dispatchEvent({
      type: ActionTypes.AirportToggled,
      value: newValue
    });
    setValue(newValue);
  };
  const setValueHandler = nextValue => {
    if (!isEqual(value, nextValue)) {
      dispatchEvent({
        type: ActionTypes.FunctionValueChanged,
        value: nextValue
      });
      setValue(nextValue);
    }
  };
  const checkValidity = () => true; // TODO: last implementation was returning always true anyway
  /**
   * WIDGET SETUP
   * */
  const overlay = useWidgetOverlay({
    open: widgetOpen,
    type: widgetType,
    options: floatingOptions,
    dimming,
    onOpenChange: handleOpenChange,
    layout
  });
  const getFieldProps = () => ({
    ...overlay.getReferenceProps(),
    value,
    config: {
      airports,
      countryCode,
      forcedNeutralLabel
    },
    active: widgetOpen,
    invalid,
    disabled
  });
  const getWidgetProps = () => ({
    ...overlay.getWidgetProps(),
    type: widgetType,
    open: widgetOpen,
    data: {
      countryList: retrieveData(),
      nearbyAirports: airports
    },
    handlers: {
      onApply: handleApply,
      onCheck: handleCheck,
      onReset: handleReset,
      onClose: handleClose,
      onInputChange: handleSuggestionInputChange,
      isChecked // @FIXME we should pass the list of selected codes to the widget instead
    },
    config: {
      countryCode,
      enableSelectAll,
      showSuggestions
    },
    widgetHead,
    layout,
    dimming
  });
  return {
    isActive: widgetOpen,
    isInvalid: invalid,
    isDisabled: disabled,
    getFieldProps,
    getWidgetProps,
    setValue: setValueHandler,
    checkValidity
  };
};